import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

interface Actualite {
  id: string;
  titre: string;
  description: string;
  contenu: string;
  type: string;
  image_url: string | null;
  epingle: boolean;
  date_creation: string;
}

// Clé volontairement SANS le préfixe "csf-" pour ne pas être purgée
// lors de la déconnexion (contrairement aux données de santé).
const SEEN_KEY = 'actualites-vues-public';

function getSeenIds(): string[] {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function markSeen(id: string) {
  const seen = getSeenIds();
  if (!seen.includes(id)) {
    seen.push(id);
    localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  }
}

const typeLabels: Record<string, string> = {
  evenement: 'Événement',
  don_sang: 'Don de sang',
  campagne: 'Campagne',
  information: 'Information',
  sante: 'Santé',
  autre: 'Autre',
};

const typeColors: Record<string, string> = {
  evenement: 'bg-purple-500',
  don_sang: 'bg-red-500',
  campagne: 'bg-blue-500',
  information: 'bg-green-500',
  sante: 'bg-green-500',
  autre: 'bg-gray-500',
};

export default function PublicActualiteNotifier() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [actualites, setActualites] = useState<Actualite[]>([]);
  const [popupActualite, setPopupActualite] = useState<Actualite | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (user) return;
    let cancelled = false;

    (async () => {
      try {
        const [actRes, cfgRes] = await Promise.all([
          supabase
            .from('actualites')
            .select('id, titre, description, contenu, type, image_url, epingle, date_creation')
            .eq('actif', true)
            .order('epingle', { ascending: false })
            .order('date_creation', { ascending: false }),
          supabase
            .from('system_config')
            .select('config_value')
            .eq('config_key', 'actualites_config')
            .maybeSingle(),
        ]);

        if (cancelled) return;

        const { data, error } = actRes;
        if (error) return;

        const list = (data || []) as Actualite[];
        setActualites(list);

        const featuredId: string | null = cfgRes.data?.config_value?.featured_id ?? null;

        const seen = getSeenIds();
        const unseen = list.filter((a) => !seen.includes(a.id));

        // Priorité à l'actualité mise en avant, sinon la plus récente non vue.
        let chosen: Actualite | null = null;
        if (featuredId) {
          chosen = unseen.find((a) => a.id === featuredId) || null;
        }
        if (!chosen) {
          chosen = unseen[0] || null;
        }

        if (chosen) {
          setPopupActualite(chosen);
          setShowModal(true);
        }
      } catch (err) {
        console.error('Erreur chargement des actualités (notifier):', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Ne rien afficher si un utilisateur est connecté ou s'il n'y a aucune actualité.
  if (user || actualites.length === 0) return null;

  const handleClose = () => {
    if (popupActualite) {
      markSeen(popupActualite.id);
    }
    setShowModal(false);
  };

  const handleView = () => {
    if (popupActualite) {
      markSeen(popupActualite.id);
      navigate(`/actualites/${popupActualite.id}`);
    }
    setShowModal(false);
  };

  return (
    <>
      {/* === FENÊTRE POP-UP DE L'ACTUALITÉ === */}
      {showModal && popupActualite && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
          ></div>

          <div className="relative bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl animate-fadeIn max-h-[90vh] overflow-y-auto">
            {/* Liseré décoratif */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-clinic-blue to-clinic-green"></div>

            {/* Bouton fermer */}
            <button
              onClick={handleClose}
              aria-label="Fermer"
              className="absolute top-4 right-4 z-10 w-9 h-9 bg-white/90 hover:bg-white rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 shadow-md transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {/* Image uploadée */}
            {popupActualite.image_url && (
              <div className="w-full bg-gradient-to-br from-clinic-blue/10 to-clinic-green/10 flex items-center justify-center">
                <img
                  src={popupActualite.image_url}
                  alt={popupActualite.titre}
                  className="w-full h-auto max-h-56 md:max-h-64 object-contain"
                />
              </div>
            )}

            {/* Contenu */}
            <div className="p-6 md:p-8">
              <div className="flex items-center gap-2 mb-4">
                <span
                  className={`${typeColors[popupActualite.type] || 'bg-gray-500'} text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap`}
                >
                  <i className="ri-megaphone-line"></i>
                  {typeLabels[popupActualite.type] || popupActualite.type}
                </span>
                <span className="text-xs text-gray-400 font-medium uppercase tracking-wide whitespace-nowrap">
                  Nouvelle annonce
                </span>
              </div>

              <h2 className="text-xl md:text-2xl font-bold text-clinic-blue-dark mb-3 leading-tight">
                {popupActualite.titre}
              </h2>

              <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6">
                {popupActualite.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleView}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-clinic-blue to-clinic-green text-white rounded-full font-semibold text-sm md:text-base hover:opacity-90 transition-all shadow-md whitespace-nowrap cursor-pointer"
                >
                  Voir l'actualité
                  <i className="ri-arrow-right-line"></i>
                </button>
                <button
                  onClick={handleClose}
                  className="px-6 py-3 border-2 border-gray-200 text-gray-600 rounded-full font-semibold text-sm md:text-base hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Plus tard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}